import dayjs, { type Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export type ScheduleType = 'daily' | 'weekly' | 'monthly';

export interface ScheduleConfig {
    schedule: ScheduleType;
    weekStart?: number;
}

export interface ScheduleWindow {
    start: string;
    end?: string;
}

export interface DateRange {
    start: Dayjs;
    end: Dayjs;
}

export interface DateOption {
    value: string;
    range: DateRange;
    label: string;
}

export interface ScheduleDay<T> {
    key: string;
    label: string;
    items: T[];
}

function endOf(window: ScheduleWindow, timeZone: string): Dayjs {
    if (window.end) return dayjs(window.end).tz(timeZone);
    return dayjs(window.start).tz(timeZone).endOf('day');
}

export function hasEnded(window: ScheduleWindow, now: Dayjs, timeZone: string): boolean {
    return !now.isBefore(endOf(window, timeZone));
}

export function isLive(window: ScheduleWindow, now: Dayjs): boolean {
    if (!window.end) return false;
    return !now.isBefore(window.start) && now.isBefore(window.end);
}

export function isWithin(date: string, range: DateRange): boolean {
    return !dayjs(date).isBefore(range.start) && !dayjs(date).isAfter(range.end);
}

function weekStartOf(day: Dayjs, weekStart?: number): Dayjs {
    if (weekStart === undefined) return day.startOf('day');
    return day.subtract((day.day() - weekStart + 7) % 7, 'day').startOf('day');
}

const DAY_FORMAT = 'dddd, MMMM D';

function dayLabel(start: Dayjs, now: Dayjs): string {
    if (start.isSame(now, 'day')) return start.format('[Today], MMMM D');
    return start.format(DAY_FORMAT);
}

function weekLabel(start: Dayjs, end: Dayjs): string {
    if (start.isSame(end, 'month')) return `${start.format('MMM D')} - ${end.format('D')}`;
    return `${start.format('MMM D')} - ${end.format('MMM D')}`;
}

const SCHEDULE_OPTIONS: Record<
    ScheduleType,
    (date: Dayjs, config: ScheduleConfig, now: Dayjs) => Omit<DateOption, 'value'>
> = {
    daily: (date, _config, now) => {
        const start = date.startOf('day');
        return { range: { start, end: date.endOf('day') }, label: dayLabel(start, now) };
    },
    weekly: (date, config) => {
        const start = weekStartOf(date, config.weekStart);
        const end = start.add(6, 'day').endOf('day');
        return { range: { start, end }, label: weekLabel(start, end) };
    },
    monthly: (date) => {
        const start = date.startOf('month');
        return { range: { start, end: date.endOf('month') }, label: start.format('MMMM') };
    },
};

export function buildDateOptions(
    windows: ScheduleWindow[],
    config: ScheduleConfig,
    now: Dayjs,
    timeZone: string,
): DateOption[] {
    const options = new Map<string, DateOption>();

    windows
        .filter((window) => !hasEnded(window, now, timeZone))
        .map((window) => dayjs(window.start).tz(timeZone))
        .sort((a, b) => a.valueOf() - b.valueOf())
        .forEach((date) => {
            const option = SCHEDULE_OPTIONS[config.schedule](date, config, now);
            const value = option.range.start.format('YYYY-MM-DD');
            options.set(value, options.get(value) ?? { ...option, value });
        });

    return [...options.values()];
}

export function groupByDay<T>(items: T[], startOf: (item: T) => string, timeZone: string): ScheduleDay<T>[] {
    const days = new Map<string, ScheduleDay<T>>();

    items
        .toSorted((a, b) => dayjs(startOf(a)).valueOf() - dayjs(startOf(b)).valueOf())
        .forEach((item) => {
            const date = dayjs(startOf(item)).tz(timeZone);
            const key = date.format('YYYY-MM-DD');
            const day = days.get(key) ?? { key, label: date.format(DAY_FORMAT), items: [] };
            day.items.push(item);
            days.set(key, day);
        });

    return Array.from(days.values());
}

export function formatStartTime(start: string, timeZone: string, tbd?: boolean): string {
    if (tbd) return 'TBD';
    return dayjs(start).tz(timeZone).format('h:mm A');
}

const ZONE_ABBREVIATION = /^[A-Z]{2,4}$/;

export function timeZoneName(timeZone: string, at: Date = new Date()): string {
    const generic = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortGeneric' })
        .formatToParts(at)
        .find((part) => part.type === 'timeZoneName')?.value;
    if (generic && ZONE_ABBREVIATION.test(generic)) return generic;
    return dayjs(at).tz(timeZone).format('z');
}
